export interface CVPersonalInfo {
  fullName: string;
  jobTitle: string;
  email: string;
  phone: string;
  location: string;
  website: string;
  linkedin: string;
  github: string;
  summary: string;
  photoUrl: string;
  gdprConsent: boolean;
  aboutTitle?: string;
}

export type CVSectionType =
  | 'experience'
  | 'education'
  | 'skills'
  | 'projects'
  | 'certifications'
  | 'languages'
  | 'hobbies'
  | 'interests';

export interface CVEntry {
  id: string;
  title: string;
  subtitle: string;
  location: string;
  startDate: string;
  endDate: string;
  description: string;
  url: string;
  urlLabel: string;
  previewUrl: string;
  previewUrlLabel: string;
  tags: string[];
}

export interface CVSection {
  id: string;
  type: CVSectionType;
  title: string;
  entries: CVEntry[];
}

export interface CVData {
  personalInfo: CVPersonalInfo;
  sections: CVSection[];
  aboutPosition?: number;
}

export type CVFontFamily = 'Lato' | 'Roboto' | 'Inter' | 'Merriweather' | 'Poppins';

export type CVFontSize = 'small' | 'medium' | 'large';

export type CVTemplate = 'classic' | 'modern' | 'creative';

export interface CVSettings {
  fontFamily: CVFontFamily;
  accentColor: string;
  accentOpacity: number;
  fontSize: CVFontSize;
  headerBackground: boolean;
  sidebarReversed: boolean;
  showPhoto: boolean;
}

export interface CVAIReportSection {
  sectionTitle: string;
  rating: 'strong' | 'moderate' | 'needs-work';
  feedback: string;
}

export interface CVAIReport {
  atsScore: number;
  sectionBreakdown: CVAIReportSection[];
  missingKeywords: string[];
  suggestions: string[];
  overallSummary: string;
  generatedAt: string;
}

export interface UserCV {
  id: string;
  user_id: string;
  title: string;
  template: CVTemplate;
  data: CVData;
  settings: CVSettings;
  ai_report: CVAIReport | null;
  is_copy: boolean;
  is_shared: boolean;
  source_cv_id: string | null;
  created_at: string;
  updated_at: string;
}

export const CV_LIMIT = 5;

export const CV_FONT_OPTIONS: { value: CVFontFamily; label: string }[] = [
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Inter', label: 'Inter' },
  { value: 'Merriweather', label: 'Merriweather' },
];

export const CV_ACCENT_COLORS = [
  '#6B46C1', // purple (BDS brand)
  '#1A202C', // near-black
  '#2D3748', // gray-dark
  '#2B6CB0', // blue
  '#2F855A', // green
  '#C05621', // orange
  '#C53030', // red
  '#277C7A', // teal (darkened for WCAG AA white-text contrast)
];

export const CV_SECTION_DEFAULTS: Record<CVSectionType, string> = {
  experience: 'Work Experience',
  education: 'Education',
  skills: 'Skills',
  projects: 'Projects',
  certifications: 'Certifications',
  languages: 'Languages',
  hobbies: 'Hobbies',
  interests: 'Interests',
};

export const GDPR_CONSENT_TEXT =
  'I agree to the processing of my personal data contained in this document for the purposes of this and future recruitment processes, in accordance with the Personal Data Protection Act of 10 May 2018 (as amended) and Regulation (EU) 2016/679 of the European Parliament and of the Council of 27 April 2016 (GDPR).';

export const ROLE_SUGGESTIONS = [
  'Frontend Developer',
  'Full-Stack Developer',
  'Backend Developer',
  'DevOps Engineer',
  'UX/UI Designer',
  'Graphic Designer',
  'Data Scientist',
  'Mobile Developer',
];
